import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '@/context';
import type { DicomImportResult, DicomMetadata } from '@/lib/dicom';
import { CHUNK_SIZE } from '@/lib/file-utils';
import { errorIds, logError } from '@/lib/log-error';
import { ApiError } from '@/services';
import { isDedupHit } from '@/services/data.service';

const VOLUME_FILENAME = 'volume.mha';
const MAIN_MHA_CHUNK_SIZE = CHUNK_SIZE;
const MAIN_MHA_CONTENT_TYPE = 'application/octet-stream';

/**
 * Per-chunk retry budget for transient failures (5xx, generic
 * network errors). Lower-status responses (4xx, 410, abort) are
 * surfaced immediately because they will not recover with a retry.
 */
const CHUNK_MAX_ATTEMPTS = 3;
const CHUNK_BACKOFF_MS = [200, 600, 1_500];

/**
 * Discriminator for the distinct error families the eager upload can
 * encounter. Lets the UI react differently to each — most importantly
 * `commit_conflict` (sibling browser tab finished first), which can be
 * presented as success rather than failure.
 */
export type EagerUploadFailureReason =
    | 'session_expired' // 410 from initiate/upload/commit
    | 'session_not_found' // 404 (e.g. session aborted from another tab)
    | 'commit_conflict' // 409 P2002 dedup race
    | 'network' // exhausted CHUNK_MAX_ATTEMPTS retries
    | 'unknown';

export type EagerUploadStatus =
    | { kind: 'idle' }
    | {
          kind: 'uploading';
          progress: number;
          phase: 'initiating' | 'chunks' | 'committing';
      }
    | {
          kind: 'success';
          inputDatasetId: string;
          patientId: string | null;
          patientName: string | null;
          /** True when the server short-circuited via contentSha256 dedup. */
          dedupHit: boolean;
      }
    | { kind: 'aborted' }
    | { kind: 'error'; reason: EagerUploadFailureReason; message: string };

export interface EagerUploadResult {
    inputDatasetId: string;
    patientId: string | null;
    patientName: string | null;
    dedupHit: boolean;
}

export interface UseEagerDicomUploadResult {
    status: EagerUploadStatus;
    /**
     * Stream the reconstructed volume to the server, populating the
     * `InputDataset` row with the worker-extracted DICOM Q/R metadata
     * so the new row appears in the catalogue with full patient /
     * study / series context.
     */
    upload: (args: {
        workflowId: string;
        slotKey: string;
        result: DicomImportResult;
    }) => Promise<EagerUploadResult | null>;
    /**
     * Cancel an in-flight upload. The current XHR is aborted via
     * AbortSignal and status moves to `aborted`. Safe to call when no
     * upload is in flight — it's a no-op.
     */
    abort: () => void;
    /** Reset the status to `idle` (e.g. when the Sheet closes). */
    reset: () => void;
}

/**
 * Eager DICOM upload from the wizard's reconstruction sheet.
 *
 * Compared to the legacy "upload at submit time" flow this hook:
 *   - Sends bytes the moment reconstruction completes, so multi-slot
 *     workflows do not stack multi-GB `ArrayBuffer`s in browser memory.
 *   - Populates the `InputDataset` row with the worker's DICOM Q/R
 *     metadata, so the new entry shows up in the catalogue picker with
 *     full patient/study/series context (patient lock works, group
 *     headers render, etc.).
 *   - Returns the new `inputDatasetId` so the wizard can treat the
 *     reconstruction as just another slot pick — submit collapses to
 *     `POST /cases { inputs }` with no per-slot byte loop.
 *   - Retries each chunk on transient failures, surfaces distinct
 *     error reasons (session_expired, commit_conflict, network,
 *     session_not_found, unknown), and exposes an `abort()` method so
 *     the wizard's Sheet can offer Cancel mid-upload instead of
 *     silently blocking close.
 */
export function useEagerDicomUpload(): UseEagerDicomUploadResult {
    const api = useApi();
    const [status, setStatus] = useState<EagerUploadStatus>({ kind: 'idle' });

    // Keeps the currently-active AbortController so abort() can cancel
    // the in-flight request even though it was started in a useCallback
    // closure. A ref is the right tool — we don't want re-renders when
    // the controller swaps.
    const controllerRef = useRef<AbortController | null>(null);

    // Cancel any in-flight upload if the consuming component unmounts.
    // The earlier implementation kept firing chunks after the Sheet was
    // closed, eventually producing an orphan dataset and a "setState on
    // unmounted component" warning. The cleanup here is the only abort
    // path that runs without an explicit user gesture.
    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
        };
    }, []);

    const upload = useCallback<UseEagerDicomUploadResult['upload']>(
        async ({ workflowId, slotKey, result }) => {
            // Replace any previous controller — `upload` is allowed to be
            // re-invoked after a reset() and we don't want the old one
            // to remain wired to an abandoned status closure.
            controllerRef.current?.abort();
            const controller = new AbortController();
            controllerRef.current = controller;
            const signal = controller.signal;

            setStatus({ kind: 'uploading', progress: 0, phase: 'initiating' });

            try {
                const dicom = toCommitDicom(result.metadata);

                const session = await api.data.initiateUpload(
                    {
                        workflowId,
                        slotKey,
                        filename: VOLUME_FILENAME,
                        contentType: MAIN_MHA_CONTENT_TYPE,
                        totalSize: result.mhaBuffer.byteLength,
                        chunkSize: MAIN_MHA_CHUNK_SIZE,
                        sha256Checksum: result.sha256,
                        contentSha256: result.sha256,
                    },
                    signal
                );

                if (isDedupHit(session)) {
                    const patientId = result.metadata.patientId;
                    const patientName = result.metadata.patientName;
                    setStatus({
                        kind: 'success',
                        inputDatasetId: session.inputDatasetId,
                        patientId,
                        patientName,
                        dedupHit: true,
                    });
                    return {
                        inputDatasetId: session.inputDatasetId,
                        patientId,
                        patientName,
                        dedupHit: true,
                    };
                }

                const totalSize = result.mhaBuffer.byteLength;
                const totalChunks = Math.ceil(totalSize / MAIN_MHA_CHUNK_SIZE);
                const blockIds: string[] = [];

                setStatus({ kind: 'uploading', progress: 0, phase: 'chunks' });
                for (let i = 0; i < totalChunks; i++) {
                    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
                    const start = i * MAIN_MHA_CHUNK_SIZE;
                    const end = Math.min(start + MAIN_MHA_CHUNK_SIZE, totalSize);
                    const chunk = result.mhaBuffer.slice(start, end);
                    const part = await uploadChunkWithRetry(
                        () => api.data.uploadChunk(session.uploadId, i, chunk, signal),
                        signal
                    );
                    blockIds.push(part.blockId);
                    setStatus({
                        kind: 'uploading',
                        progress: Math.round(((i + 1) / totalChunks) * 100),
                        phase: 'chunks',
                    });
                }

                setStatus({ kind: 'uploading', progress: 100, phase: 'committing' });
                const commitResult = await api.data.commitUpload(
                    session.uploadId,
                    { blockIds, sha256Checksum: result.sha256, dicom },
                    result.previewBuffer,
                    signal
                );

                const patientId = result.metadata.patientId;
                const patientName = result.metadata.patientName;
                setStatus({
                    kind: 'success',
                    inputDatasetId: commitResult.inputDatasetId,
                    patientId,
                    patientName,
                    dedupHit: false,
                });
                return {
                    inputDatasetId: commitResult.inputDatasetId,
                    patientId,
                    patientName,
                    dedupHit: false,
                };
            } catch (err) {
                // If the ref no longer points to *our* controller, the
                // hook was either reset() or a new upload was started —
                // the caller has already set the next status and we
                // must not clobber it.
                if (controllerRef.current !== controller) {
                    return null;
                }
                if (isAbortError(err) || signal.aborted) {
                    setStatus({ kind: 'aborted' });
                    return null;
                }
                const { reason, message } = classifyUploadError(err);
                logError(errorIds.EAGER_UPLOAD_FAILED, message, {
                    workflowId,
                    slotKey,
                    reason,
                    error: err,
                });
                setStatus({ kind: 'error', reason, message });
                return null;
            } finally {
                if (controllerRef.current === controller) {
                    controllerRef.current = null;
                }
            }
        },
        [api]
    );

    const abort = useCallback(() => {
        controllerRef.current?.abort();
    }, []);

    const reset = useCallback(() => {
        // Null the ref *before* aborting so the upload's catch sees
        // the swap and skips its terminal-status write — otherwise the
        // catch races setStatus({kind:'idle'}) with setStatus({kind:'aborted'}).
        const current = controllerRef.current;
        controllerRef.current = null;
        current?.abort();
        setStatus({ kind: 'idle' });
    }, []);

    return { status, upload, abort, reset };
}

/**
 * Run a single-chunk upload with a tight retry budget. Network /
 * server-side transient failures (5xx, fetch rejections, generic
 * `Error`) earn up to {@link CHUNK_MAX_ATTEMPTS} attempts with
 * exponential backoff. 4xx responses are surfaced immediately because
 * the server will not change its mind without a new session.
 */
async function uploadChunkWithRetry<T>(call: () => Promise<T>, signal: AbortSignal): Promise<T> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= CHUNK_MAX_ATTEMPTS; attempt++) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        try {
            return await call();
        } catch (err) {
            if (isAbortError(err) || signal.aborted) throw err;
            // 4xx responses (other than 408 Request Timeout / 429 Too
            // Many Requests) are deterministic — retrying won't help.
            // 410 Gone in particular means the session expired.
            if (err instanceof ApiError && !isRetryableStatus(err.status)) {
                throw err;
            }
            lastError = err;
            if (attempt === CHUNK_MAX_ATTEMPTS) break;
            await sleep(CHUNK_BACKOFF_MS[attempt - 1] ?? 1_500, signal);
        }
    }
    throw lastError;
}

function isRetryableStatus(status: number): boolean {
    if (status >= 500) return true;
    return status === 408 || status === 429;
}

function isAbortError(err: unknown): boolean {
    return err instanceof DOMException && err.name === 'AbortError';
}

function classifyUploadError(err: unknown): { reason: EagerUploadFailureReason; message: string } {
    if (err instanceof ApiError) {
        if (err.status === 410) return { reason: 'session_expired', message: err.message };
        if (err.status === 404) return { reason: 'session_not_found', message: err.message };
        if (err.status === 409) return { reason: 'commit_conflict', message: err.message };
        if (err.status >= 500) return { reason: 'network', message: err.message };
        return { reason: 'unknown', message: err.message };
    }
    if (err instanceof Error) {
        // Native fetch network failures arrive as TypeError with
        // message 'Failed to fetch'. Treat them as transient — by the
        // time we land here, the per-chunk retry budget is exhausted.
        if (err.name === 'TypeError') return { reason: 'network', message: err.message };
        return { reason: 'unknown', message: err.message };
    }
    return { reason: 'unknown', message: 'Upload failed' };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        };
        if (signal.aborted) {
            onAbort();
            return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
    });
}

/**
 * Project `DicomMetadata` from the worker into the wire-format
 * `dicom` block of `ICommitUpload`. Drops null / empty values so the
 * server-side DTO only validates the fields the worker actually
 * extracted.
 */
export function toCommitDicom(meta: DicomMetadata) {
    type CommitDicom = NonNullable<Parameters<ReturnType<typeof useApi>['data']['commitUpload']>[1]['dicom']>;
    const out: Record<string, unknown> = {};
    if (meta.patientName) out.patientName = meta.patientName;
    if (meta.patientId) out.patientId = meta.patientId;
    if (meta.patientBirthDate) out.patientBirthDate = meta.patientBirthDate;
    if (meta.patientSex && ['M', 'F', 'O', 'U'].includes(meta.patientSex)) {
        out.patientSex = meta.patientSex;
    }
    if (meta.studyInstanceUid) out.studyInstanceUid = meta.studyInstanceUid;
    if (meta.studyDate) out.studyDate = meta.studyDate;
    if (meta.studyTime) out.studyTime = meta.studyTime;
    if (meta.accessionNumber) out.accessionNumber = meta.accessionNumber;
    if (meta.studyDescription) out.studyDescription = meta.studyDescription;
    if (meta.referringPhysicianName) out.referringPhysicianName = meta.referringPhysicianName;
    if (meta.seriesInstanceUid) out.seriesInstanceUid = meta.seriesInstanceUid;
    if (meta.seriesDescription) out.seriesDescription = meta.seriesDescription;
    if (meta.seriesNumber != null) out.seriesNumber = meta.seriesNumber;
    if (meta.modality) out.modality = meta.modality;
    if (meta.bodyPartExamined) out.bodyPartExamined = meta.bodyPartExamined;
    if (meta.sliceThicknessMm != null) out.sliceThicknessMm = meta.sliceThicknessMm;
    if (meta.spacingBetweenSlicesMm != null) out.spacingBetweenSlicesMm = meta.spacingBetweenSlicesMm;
    if (meta.convolutionKernel) out.convolutionKernel = meta.convolutionKernel;
    if (meta.contrastBolusAgent) out.contrastBolusAgent = meta.contrastBolusAgent;
    if (meta.instanceCount > 0) out.instanceCount = meta.instanceCount;
    return out as CommitDicom;
}
